import React, { FC } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';
import { ReactComponent as MailIcon } from '@blink/assets/src/icons/mail.svg';
import { ReactComponent as LoaderDashWhite } from '@blink/assets/src/icons/loaderDashWhite.svg';
import { yupResolver } from '@hookform/resolvers/yup';

import { useModal, useOrganizationId, useSendInvites } from '@/features/Modals';
import { Button, ButtonVariant, Input } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';
import inviteMembersSchema, { InviteMembersData } from '@/shared/validations/inviteMembers.schema';

enum InviteMembersFields {
    NAME = 'name',
    EMAIL = 'email',
    INVITES = 'invites',
}

const LIMIT_INVITES = 10;

export const InviteMembersModal: FC = () => {
    const id = useOrganizationId();
    const { sendInvites, handleCloseModal, loading } = useSendInvites();

    const { modalProps } = useModal();

    const { emails, refresh } = modalProps;

    const {
        control,
        watch,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<InviteMembersData>({
        mode: 'onChange',
        resolver: yupResolver(inviteMembersSchema),
        context: {
            emails: emails || [],
        },
        defaultValues: {
            name: '',
            email: '',
            invites: [],
        } as InviteMembersData,
    });

    const nameValue = watch(InviteMembersFields.NAME);
    const emailValue = watch(InviteMembersFields.EMAIL);
    const invites = watch(InviteMembersFields.INVITES);

    const handleCancel = () => {
        handleCloseModal();
    };

    const handleAddEmail = () => {
        const name = getValues(InviteMembersFields.NAME).trim();
        const email = getValues(InviteMembersFields.EMAIL).trim();

        if (
            name &&
            email &&
            !invites.some((invite) => invite.email === email) &&
            invites.length < LIMIT_INVITES
        ) {
            setValue(InviteMembersFields.INVITES, [...invites, { name, email }]);
            setValue(InviteMembersFields.NAME, '');
            setValue(InviteMembersFields.EMAIL, '');
        }
    };

    const handleRemoveEmail = (email: string) => {
        setValue(
            InviteMembersFields.INVITES,
            invites.filter((invite) => invite.email !== email),
        );
    };

    const handleSendInvites = () => {
        sendInvites({
            id,
            invites,
            refresh,
        });
    };

    return (
        <div className='flex flex-col w-[600px] min-h-[210px] mt-7'>
            <div className='flex gap-3 mb-3'>
                <div className='flex flex-col gap-[6px] flex-[1_0_0]'>
                    <div className="overflow-hidden text-content-primary [font-feature-settings:'calt_off'] text-ellipsis text-[14px] font-medium leading-[20px] tracking-[-0.42px] whitespace-nowrap">
                        User Name
                    </div>
                    <Controller
                        name={InviteMembersFields.NAME}
                        control={control}
                        rules={{
                            required: 'User Name is required',
                            minLength: {
                                value: 2,
                                message: 'Name must be at least 2 characters',
                            },
                        }}
                        render={({ field }) => (
                            <Input
                                {...field}
                                type='text'
                                placeholder='Enter user name...'
                                color={Input.colors.Default}
                                className='w-full px-3 h-[44px] border font-medium placeholder:text-content-fourth text-content-fourth'
                            />
                        )}
                    />
                    <div
                        className={cn('text-content-negative text-sm min-h-[37px]', {
                            invisible: !errors.name,
                            visible: !!errors.name,
                        })}
                    >
                        {errors?.name?.message}
                    </div>
                </div>

                <div className='flex flex-col gap-[6px] flex-[1_0_0]'>
                    <div className="overflow-hidden text-content-primary [font-feature-settings:'calt_off'] text-ellipsis text-[14px] font-medium leading-[20px] tracking-[-0.42px] whitespace-nowrap">
                        User Email
                    </div>
                    <Controller
                        name={InviteMembersFields.EMAIL}
                        control={control}
                        rules={{
                            required: 'Email is required',
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: 'Invalid email address',
                            },
                        }}
                        render={({ field }) => (
                            <Input
                                {...field}
                                type='text'
                                placeholder='Enter user email...'
                                color={Input.colors.Default}
                                className='w-full px-3 h-[44px] border font-medium placeholder:text-content-fourth text-content-fourth'
                            />
                        )}
                    />
                    <div
                        className={cn('text-content-negative text-sm min-h-[10px]', {
                            invisible: !errors.email,
                            visible: !!errors.email,
                        })}
                    >
                        {errors?.email?.message}
                    </div>
                </div>
            </div>
            {invites.length < LIMIT_INVITES && (
                <Button
                    variant={ButtonVariant.Primary}
                    className='flex items-center justify-center w-full px-4 py-[10px] gap-2 text-md disabled:bg-background-secondary disabled:opacity-100'
                    onClick={handleAddEmail}
                    disabled={
                        !nameValue ||
                        !emailValue ||
                        !!errors.email ||
                        !!errors.name ||
                        loading ||
                        invites.length >= LIMIT_INVITES
                    }
                >
                    Add User
                </Button>
            )}

            {invites.length > 0 && (
                <div className='flex flex-col items-start p-5 mt-5 gap-3 flex-[1_0_0] self-stretch rounded-[var(--radius-overall)] border border-opaque bg-background-primary'>
                    <div className="overflow-hidden text-content-primary [font-feature-settings:'calt_off'] text-ellipsis text-[14px] font-medium leading-[20px] tracking-[-0.42px] whitespace-nowrap">
                        Invites to be sent ({invites.length})
                    </div>
                    {invites.map((invite) => (
                        <div
                            key={invite.email}
                            className='flex justify-between items-center self-stretch px-[6px] py-3 pl-5 rounded-[var(--radius-overall)] bg-background-secondary'
                        >
                            <div className='flex items-center gap-4 [&>span]:text-content-secondary'>
                                <MailIcon />
                                <div className='flex items-center gap-4 [&>span]:text-content-secondary'>
                                    {invite.name} ({invite.email})
                                </div>
                            </div>
                            <Button
                                variant={ButtonVariant.Tertiary}
                                className='flex items-center justify-center px-4 py-[10px] gap-2 text-md bg-background-secondary w-auto'
                                onClick={() => handleRemoveEmail(invite.email)}
                            >
                                <CloseIcon />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div className='flex justify-end self-stretch gap-3 mt-7'>
                <Button
                    variant={ButtonVariant.Secondary}
                    className='flex items-center justify-center px-4 py-[10px] text-md'
                    onClick={handleCancel}
                >
                    Cancel
                </Button>

                <Button
                    leftIcon={loading ? <LoaderDashWhite /> : null}
                    variant={ButtonVariant.Tertiary}
                    className='flex items-center justify-center min-w-[110px] w-auto px-4 py-[10px] gap-2 text-md bg-background-accent text-content-inversePrimary hover:bg-[#4732D4]/90 disabled:bg-background-extensions disabled:text-content-extensions disabled:opacity-100'
                    disabled={invites.length === 0 || loading}
                    onClick={handleSendInvites}
                >
                    {loading ? (
                        'Loading...'
                    ) : (
                        <>
                            Send {invites.length} Invite{invites.length > 1 ? 's' : ''}
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};
